import { Fragment } from 'react';
import { KoobMapClusterer, KoobMapPolyline } from '@koob/maps';
import { koobMarker } from '@koob/map-marker';
import { getItineraryMarkers } from '../../logic/getItineraryMarkers';
import { useItineraryPinLevel } from '../../hooks/useTripStoreSelectors';
import useTripStore from '../../store/useTripStore';
import TripMapHotelMarker from './TripMapHotelMarker';
import TripMapItineraryPoiMarker from './TripMapItineraryPoiMarker';
import TripMapItineraryProgramMarker from './TripMapItineraryProgramMarker';
import type { TripMapHotelCardProps, TripMapMarkers } from './types';

const PROGRAM_ICON = koobMarker('programDay', { filled: true });
const POI_ICON = koobMarker('poi', { filled: true });

const getEndpointType = (index: number, count: number) => {
  if (index === 0) {
    return 'start';
  }

  return index === count - 1 ? 'flag' : null;
};

interface TripMapItineraryProps {
  days: any[];
  showHotels: boolean;
  markers: TripMapMarkers;
  hotelCardProps: TripMapHotelCardProps;
}

// The trip as a route: the start of each experience linked by a line, the
// hotels, and (by pin level) the programs and their points of interest.
export default function TripMapItinerary({
  days,
  showHotels,
  markers,
  hotelCardProps,
}: TripMapItineraryProps) {
  const itineraryPinLevel = useItineraryPinLevel();
  const experiencesMap = useTripStore((state) => state.experiences);
  const hotelsMap = useTripStore((state) => state.hotels);
  const { linePoints, experiences, hotels, programs } = getItineraryMarkers({
    days,
    experiencesMap,
    hotelsMap,
  });

  return (
    <>
      <KoobMapPolyline
        path={linePoints}
        strokeColor="#7c3aed"
        strokeOpacity={1}
        strokeWeight={5}
      />

      {experiences.map((experience, index) => (
        <Fragment key={'map-i-s-' + experience?.id + index}>
          {markers.renderItineraryExperienceMarker(experience, {
            typeOverride: getEndpointType(index, experiences.length),
          })}
        </Fragment>
      ))}

      {showHotels &&
        hotels.map((hotel, index) => (
          <TripMapHotelMarker
            key={'map-i-h-' + hotel?.id + index}
            hotel={hotel}
            onAdd={() => {}}
            kind="itinerary"
            hotelCardProps={hotelCardProps}
          />
        ))}

      <KoobMapClusterer>
        {itineraryPinLevel >= 1 &&
          programs.map((program, i) => (
            <Fragment key={'map-i-p-' + program.id + i}>
              <TripMapItineraryProgramMarker
                label={`${i + 1}`}
                program={program}
                icon={PROGRAM_ICON}
              />

              {itineraryPinLevel >= 2 &&
                program.pois?.map((poi: any, j: number) => (
                  <TripMapItineraryPoiMarker
                    key={'map-i-poi-' + poi.id + j}
                    label={`${i + 1}`}
                    poi={poi}
                    icon={POI_ICON}
                  />
                ))}
            </Fragment>
          ))}
      </KoobMapClusterer>
    </>
  );
}
